import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  CircularProgress,
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword } from 'firebase/auth';
import { employerApi } from '../../services/apiClient';
import { toast } from 'react-toastify';

const Register = () => {
  console.log('🎯 Register component mounted');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(null);
  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🎯 Register useEffect: Checking for token in URL');

    const inviteToken = searchParams.get('token');
    if (!inviteToken) {
      setError('No invitation token provided');
      setValidating(false);
      return;
    }

    setToken(inviteToken);
    validateToken(inviteToken);
  }, [searchParams]);

  const validateToken = async (inviteToken: string) => {
    try {
      setValidating(true);
      setError(null);

      const response = await employerApi.post('/validate-invite', { token: inviteToken });

      console.log('🔍 Validate invite response:', response);

      if (response.valid) {
        // Check if user needs password setup (CyclePass user)
        if (response.needsPasswordSetup) {
          console.log('🔐 User needs to set password', {
            email: response.email,
            hasCyclePassAccess: response.hasCyclePassAccess
          });
          setInviteData({
            ...response,
            isPasswordSetup: true // Flag to show password setup UI
          });
          setEmail(response.email);
          return;
        }

        // Check if user already exists and needs to login instead
        if (response.userExists && response.needsLogin) {
          console.log('👤 User already exists, redirecting to login', {
            authProvider: response.authProvider,
            email: response.email
          });

          // Redirect to login page with email pre-filled and auth provider info
          const loginUrl = `/login?email=${encodeURIComponent(response.email)}&message=${encodeURIComponent(`You already have an account. Please sign in to access ${response.companyName}'s employer portal.`)}&authProvider=${encodeURIComponent(response.authProvider || 'unknown')}`;
          navigate(loginUrl);
          return;
        }

        // User doesn't exist or needs registration
        setInviteData(response);
        setEmail(response.email);
      } else {
        setError(response.message || 'Invalid invitation');
      }
    } catch (err: any) {
      console.error('Error validating token:', err);
      setError(err.response?.data?.message || err.response?.message || 'Invalid or expired invitation');
    } finally {
      setValidating(false);
    }
  };

  const handlePasswordSetup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      toast.error(pwdError);
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }

    try {
      setRegistering(true);

      // Call set-password endpoint
      const response = await employerApi.post('/set-password', {
        inviteToken: token,
        password: password
      });

      if (response.success) {
        toast.success('Password set successfully! Redirecting to login...');
        setTimeout(() => {
          navigate(`/login?email=${encodeURIComponent(email)}&message=${encodeURIComponent('Password set successfully. Please sign in to access the employer portal.')}`);
        }, 1500);
      } else {
        throw new Error(response.message || 'Failed to set password');
      }
    } catch (err: any) {
      console.error('Error setting password:', err);
      toast.error(err.response?.data?.message || err.response?.message || 'Failed to set password');
    } finally {
      setRegistering(false);
    }
  };

  const completeRegistration = async (firebaseUser: any) => {
    try {
      // Get Firebase ID token
      const idToken = await firebaseUser.getIdToken();

      console.log('🔐 Completing registration with:', {
        hasToken: !!idToken,
        tokenLength: idToken?.length,
        inviteToken: token?.substring(0, 20) + '...',
        firebaseUID: firebaseUser.uid
      });

      // Complete registration on backend
      const response = await employerApi.post('/complete-registration',
        { inviteToken: token },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      console.log('✅ Registration response:', response);

      if (response.success) {
        toast.success('Registration completed! Redirecting...');
        setTimeout(() => {
          navigate('/employees');
        }, 1500);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (err: any) {
      console.error('Error completing registration:', err);
      toast.error(err.response?.message || 'Failed to complete registration');
      // Sign out the user since registration failed
      await auth.signOut();
      throw err;
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setRegistering(true);
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      provider.setCustomParameters({
        prompt: 'select_account',
        login_hint: email // Pre-fill with expected email
      });

      let result;
      try {
        result = await signInWithPopup(auth, provider);
      } catch (signInError: any) {
        // Check if error is due to existing account with different provider
        if (signInError.code === 'auth/account-exists-with-different-credential') {
          toast.error(
            `An account already exists with ${email}. Please use email/password sign-in instead, or contact support.`,
            { autoClose: 8000 }
          );
          setRegistering(false);
          return;
        }
        throw signInError;
      }

      // Reload user to get fresh data
      await result.user.reload();
      const currentUser = auth.currentUser;

      console.log('🔍 Google Sign-In Result (after reload):', {
        user: currentUser,
        email: currentUser?.email,
        providerData: currentUser?.providerData,
        metadata: currentUser?.metadata
      });

      // Try multiple sources for email
      const tokenResponse = (result as any)._tokenResponse;
      const userEmail = currentUser?.email ||
        currentUser?.providerData?.[0]?.email ||
        tokenResponse?.email;

      console.log('📧 Email from user:', currentUser?.email);
      console.log('📧 Email from providerData:', currentUser?.providerData?.[0]?.email);
      console.log('📧 Email from tokenResponse:', tokenResponse?.email);
      console.log('📧 Full tokenResponse:', tokenResponse);
      console.log('📧 Final email to use:', userEmail);
      console.log('📧 Expected email:', email);

      // CRITICAL: Verify Firebase user object has email before proceeding
      if (!currentUser?.email) {
        console.error('❌ CRITICAL: Firebase user object missing email field');
        console.error('This should never happen with Google Sign-In');
        toast.error('Google Sign-In failed to provide email. Please try email/password signup or contact support.');
        await auth.signOut();
        return;
      }

      if (!userEmail) {
        toast.error('Could not get email from Google account. Please try email/password signup.');
        await auth.signOut();
        return;
      }

      // Verify email matches invitation
      if (userEmail.toLowerCase() !== email.toLowerCase()) {
        toast.error(`Please sign in with ${email}, not ${userEmail}`);
        await auth.signOut();
        return;
      }

      await completeRegistration(result.user);
    } catch (err: any) {
      console.error('Google signup error:', err);
      if (err.code !== 'auth/popup-closed-by-user') {
        toast.error('Failed to sign up with Google');
      }
    } finally {
      setRegistering(false);
    }
  };

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const error = validatePassword(value);
    setPasswordError(error);
    // Clear confirm password error if passwords match
    if (confirmPassword && value === confirmPassword) {
      setConfirmPasswordError(null);
    } else if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    if (value !== password) {
      setConfirmPasswordError('Passwords do not match');
    } else {
      setConfirmPasswordError(null);
    }
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      toast.error(pwdError);
      return;
    }

    // Validate password match
    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      toast.error('Passwords do not match');
      return;
    }

    try {
      setRegistering(true);

      const result = await createUserWithEmailAndPassword(auth, email, password);
      await completeRegistration(result.user);
    } catch (err: any) {
      console.error('Email signup error:', err);

      if (err.code === 'auth/email-already-in-use') {
        toast.error('This email is already registered. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        toast.error('Password is too weak. Please use a stronger password.');
      } else {
        toast.error('Failed to create account');
      }
    } finally {
      setRegistering(false);
    }
  };

  if (validating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !inviteData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
          py: 4
        }}
      >
        <Container component="main" maxWidth="xs">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: 4,
              boxShadow: '0px 10px 40px rgba(0,0,0,0.08)'
            }}
          >
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error || 'Invalid invitation'}
            </Alert>
            <Typography variant="body2" color="text.secondary" align="center">
              Please request a new invitation from your company administrator.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        py: 4
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 4,
            boxShadow: '0px 10px 40px rgba(0,0,0,0.08)'
          }}
        >
          {/* Logo and Branding */}
          <Box sx={{ mb: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              component="img"
              src={require('../../assets/logoLarge.png')}
              alt="CycleSaver Logo"
              sx={{
                height: 'auto',
                width: 'auto',
                maxHeight: '60px',
                maxWidth: '80%',
                mb: 2,
                objectFit: 'contain'
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {inviteData.isPasswordSetup ? 'Set Your Password' : `Join ${inviteData.companyName}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {inviteData.isPasswordSetup
                ? 'Set a password to access the Employer Portal'
                : 'Create your Employer Portal account'}
            </Typography>
          </Box>

          {/* Google Signup
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignup}
            disabled={registering}
            sx={{
              mb: 3,
              py: 1.5,
              borderColor: '#e0e0e0',
              color: 'text.primary',
              '&:hover': {
                borderColor: '#bdbdbd',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Continue with Google
          </Button>

          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 3 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mx: 2 }}>
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box> */}

          {/* Email/Password Signup or Password Setup */}
          <Box component="form" onSubmit={inviteData.isPasswordSetup ? handlePasswordSetup : handleEmailSignup} noValidate sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              disabled={registering}
              error={!!passwordError}
              helperText={
                passwordError || 'Must contain: at least 8 characters, 1 uppercase, 1 lowercase, 1 number, and 1 special character'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              disabled={registering}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="success"
              size="large"
              disabled={registering || !password || !confirmPassword}
              sx={{ py: 1.8, fontSize: '1rem' }}
            >
              {registering ? <CircularProgress size={24} color="inherit" /> : (inviteData.isPasswordSetup ? 'Set Password' : 'Create Account')}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            By creating an account, you agree to CycleSaver's Terms of Service and Privacy Policy
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;

